import { BusinessModelEnumLabels } from "@boundless/bl-api-client/customer/models/BusinessModelEnumLabels"
import { EstimatedRevenueEnumLabels } from "@boundless/bl-api-client/customer/models/EstimatedRevenueEnumLabels"
import { IndustryEnumLabels } from "@boundless/bl-api-client/customer/models/IndustryEnumLabels"
import { StateEnumLabels } from "@boundless/bl-api-client/customer/models/StateEnumLabels"
import { TypeOfBusinessEnumLabels } from "@boundless/bl-api-client/customer/models/TypeOfBusinessEnumLabels"
import {
  useCustomerBusinessAddressRetrieveSuspense,
  useCustomerBusinessExtraDetailsRetrieve,
} from "@boundless/bl-api-client/customer/services"
import { CountryEnumLabels } from "@boundless/bl-api-client/partners/models/CountryEnumLabels"
import { Link } from "@tanstack/react-router"

import { SummaryAccordionItem } from "@/components/summary/SummaryAccordion"
import { AccordionContent } from "@/components/ui/accordion"
import { formatDate } from "@/lib/utils"

import type {
  BusinessDetails,
  Company,
} from "@boundless/bl-api-client/customer/models"

type SummaryBusinessDetailsProps = {
  company: Company
  businessDetails: BusinessDetails
  returnTo: string
}

export function SummaryBusinessDetails({
  company,
  businessDetails,
  returnTo,
}: SummaryBusinessDetailsProps) {
  const { data: businessAddress } = useCustomerBusinessAddressRetrieveSuspense(
    company.uuid,
  )
  const { data: businessExtraDetails } =
    useCustomerBusinessExtraDetailsRetrieve(company.uuid, {
      query: {
        enabled: company.requiresExtraDetails,
      },
    })

  const shouldDisplayExtraDetails =
    company.requiresExtraDetails &&
    businessExtraDetails &&
    (businessExtraDetails.ein || businessExtraDetails.bn)

  return (
    <SummaryAccordionItem title="Business Details" value="business-details">
      <AccordionContent className="px-4 pt-2 pb-4">
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-medium">Business Information</h4>
            <Link
              className="text-primary hover:underline"
              to="/company/details"
              search={{ returnTo }}
            >
              Edit
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-y-1">
            <div className="contents">
              <div className="text-sm text-gray-600">Business Name</div>
              <div className="text-sm">{company.legalName}</div>
              <div className="text-sm text-gray-600">Trade Name</div>
              <div className="text-sm">{businessDetails.tradeName}</div>
              <div className="text-sm text-gray-600">Type of Business</div>
              <div className="text-sm">
                {businessDetails.typeOfBusiness
                  ? TypeOfBusinessEnumLabels[businessDetails.typeOfBusiness]
                  : ""}
              </div>
              <div className="text-sm text-gray-600">Business Model</div>
              <div className="text-sm">
                {businessDetails.businessModel
                  ? BusinessModelEnumLabels[businessDetails.businessModel]
                  : ""}
              </div>
              <div className="text-sm text-gray-600">Industry</div>
              <div className="text-sm">
                {businessDetails.industry
                  ? IndustryEnumLabels[businessDetails.industry]
                  : ""}
              </div>
              <div className="text-sm text-gray-600">Incorporation Date</div>
              <div className="text-sm">
                {formatDate(businessDetails.startDate)}
              </div>
              <div className="text-sm text-gray-600">
                Monthly Average Revenue
              </div>
              <div className="text-sm">
                {businessDetails.estimatedRevenue
                  ? EstimatedRevenueEnumLabels[businessDetails.estimatedRevenue]
                  : ""}
              </div>
              <div className="text-sm text-gray-600">
                Uses an online-only business bank (e.g., Brex, Mercury, Ramp)
              </div>
              <div className="text-sm">
                {businessDetails.isEbankUser ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-medium">Address Information</h4>
            <Link
              className="text-primary hover:underline"
              to="/company/address"
              search={{ returnTo }}
            >
              Edit
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-y-1">
            <div className="contents">
              <div className="text-sm text-gray-600">Incorporated Country</div>
              <div className="text-sm">
                {CountryEnumLabels[businessAddress.incorporatedCountry]}
              </div>
              <div className="text-sm text-gray-600">
                Incorporated State/Province
              </div>
              <div className="text-sm">
                {StateEnumLabels[businessAddress.incorporatedState]}
              </div>
              <div className="text-sm text-gray-600">
                Incorporated Zip Code/Postal Code
              </div>
              <div className="text-sm">
                {businessAddress.incorporatedZipCode}
              </div>
              <div className="text-sm text-gray-600">
                Incorporated Phone Number
              </div>
              <div className="text-sm">{businessAddress.incorporatedPhone}</div>
              <div className="text-sm text-gray-600">
                Incorporated Street Address
              </div>
              <div className="text-sm">
                {businessAddress.incorporatedAddress}
              </div>
              <div className="text-sm text-gray-600">
                Physical Address same as Incorporated Address
              </div>
              <div className="text-sm">
                {businessAddress.isPhysicalAddressSame ? "Yes" : "No"}
              </div>
              {!businessAddress.isPhysicalAddressSame && (
                <>
                  <div className="text-sm text-gray-600">Physical Country</div>
                  <div className="text-sm">
                    {businessAddress.physicalCountry
                      ? CountryEnumLabels[businessAddress.physicalCountry]
                      : ""}
                  </div>
                  <div className="text-sm text-gray-600">
                    Physical State/Province
                  </div>
                  <div className="text-sm">
                    {businessAddress.physicalState
                      ? StateEnumLabels[businessAddress.physicalState]
                      : ""}
                  </div>
                  <div className="text-sm text-gray-600">
                    Physical Zip Code/Postal Code
                  </div>
                  <div className="text-sm">
                    {businessAddress.physicalZipCode}
                  </div>
                  <div className="text-sm text-gray-600">
                    Physical Phone Number
                  </div>
                  <div className="text-sm">{businessAddress.physicalPhone}</div>
                  <div className="text-sm text-gray-600">
                    Physical Street Address
                  </div>
                  <div className="text-sm">
                    {businessAddress.physicalAddress}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {shouldDisplayExtraDetails && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-sm font-medium">Additional Details</h4>
              <Link
                className="text-primary hover:underline"
                to="/company/extra-details"
                search={{ returnTo }}
              >
                Edit
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-y-1">
              <div className="contents">
                {businessExtraDetails.ein && (
                  <>
                    <div className="text-sm text-gray-600">EIN</div>
                    <div className="text-sm">{businessExtraDetails.ein}</div>
                  </>
                )}
                {businessExtraDetails.bn && (
                  <>
                    <div className="text-sm text-gray-600">BN</div>
                    <div className="text-sm">{businessExtraDetails.bn}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </AccordionContent>
    </SummaryAccordionItem>
  )
}
